/**
 * Utilities for building environment-aware URLs
 */

type EnvironmentInfo = {
  env: string; // e.g., "dev", "staging", "" (production)
  tld: string; // e.g., "com", "us"
  scheme: "http" | "https";
};

/**
 * Extracts environment information from the current hostname.
 * Handles patterns like:
 * - admin.dev.junipersquare.com -> { env: "dev", tld: "com", scheme: "http" }
 * - admin.staging.junipersquare.com -> { env: "staging", tld: "com", scheme: "https" }
 * - admin.junipersquare.com -> { env: "", tld: "com", scheme: "https" }
 * - localhost:8989 -> { env: "dev", tld: "com", scheme: "http" }
 */
export function getEnvironmentInfo(): EnvironmentInfo {
  const { hostname } = window.location;

  // Handle localhost - default to dev environment
  if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
    return { env: "dev", tld: "com", scheme: "http" };
  }

  const parts = hostname.split(".");
  const tld = hostname.endsWith(".junipersquare.us") ? "us" : "com";

  // Determine environment from hostname
  // Pattern: {subdomain}.{env}.junipersquare.{tld} (e.g., admin.dev.junipersquare.com)
  // Production: {subdomain}.junipersquare.{tld} (e.g., admin.junipersquare.com)
  let env = "";
  if (parts.length >= 4 && parts[1] && parts[1] !== "junipersquare") {
    env = parts[1]; // e.g., "dev", "staging", "preview"
  }

  const scheme = env === "dev" ? "http" : "https";

  return { env, tld, scheme };
}

/**
 * Builds a full URL for an arena-specific page.
 *
 * @param arenaDomain - The arena subdomain (e.g., "rockstonecap")
 * @param path - The path within the arena (e.g., "/account/123")
 * @returns Full URL (e.g., "https://rockstonecap.staging.junipersquare.com/account/123")
 */
export function buildArenaUrl(arenaDomain: string, path: string): string {
  const { env, tld, scheme } = getEnvironmentInfo();

  // Build domain: {arenaDomain}.{env}.junipersquare.{tld} or {arenaDomain}.junipersquare.{tld}
  const domain = env
    ? `${arenaDomain}.${env}.junipersquare.${tld}`
    : `${arenaDomain}.junipersquare.${tld}`;

  // Ensure path starts with /
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  return `${scheme}://${domain}${normalizedPath}`;
}

/**
 * Builds an entity URL for the GP portal.
 *
 * @param arenaDomain - The arena subdomain (e.g., "rockstonecap")
 * @param entityId - The entity ID
 * @returns Full URL to the entity page (e.g., "https://rockstonecap.dev.junipersquare.com/account/123")
 */
export function getArenaEntityUrl(arenaDomain: string, entityId: number): string {
  return buildArenaUrl(arenaDomain, `/account/${entityId}`);
}
